import { Box } from '@mui/material';

import { isScreenTileId, sessionIdFromTileId } from '../hooks/useCallLayout';

import { ParticipantTile } from './ParticipantTile';

interface SpeakerViewProps {
  mainStageSessionId: string | null;
  sidebarParticipantIds: string[];
  activeSpeakerId: string | null;
  localSessionId: string;
  isScreenShare?: boolean;
  screenShareSessionId?: string | null;
  /** Click handler for the local screen-share preview tile. */
  onStopLocalScreenShare?: () => void;
  fillHeight?: boolean;
}

export function SpeakerView({
  mainStageSessionId,
  sidebarParticipantIds,
  activeSpeakerId,
  localSessionId,
  isScreenShare = false,
  screenShareSessionId,
  onStopLocalScreenShare,
  fillHeight = false,
}: SpeakerViewProps) {
  const mainSessionId =
    isScreenShare && screenShareSessionId ? screenShareSessionId : mainStageSessionId;

  return (
    // Outer box: size container for @container queries (cannot query itself)
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        ...(fillHeight ? { flex: 1, minHeight: 0, height: '100%' } : { aspectRatio: '16 / 9' }),
        overflow: 'hidden',
        containerType: 'size',
      }}
    >
      {/* Inner layout — responds to @container queries from the outer box */}
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'row',
          '@container (max-aspect-ratio: 4/3)': {
            flexDirection: 'column',
          },
        }}
      >
        {/* Sidebar — renders after main stage in DOM but moves above it when narrow */}
        {sidebarParticipantIds.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              width: '20%',
              minWidth: 150,
              flexShrink: 0,
              overflowY: 'auto',
              pl: 1.5,
              order: 1,
              '@container (max-aspect-ratio: 4/3)': {
                order: -1,
                flexDirection: 'row',
                alignItems: 'flex-start',
                width: '100%',
                minWidth: 0,
                flexShrink: 0,
                pl: 0,
                pb: 1,
                overflowX: 'auto',
                overflowY: 'hidden',
                // `&&` doubles the parent's class for higher specificity, so this
                // child-width rule reliably wins over a `ParticipantTile`'s own
                // `width: 100%`. Without the bump, the parent and child selectors
                // tie on specificity and source order decides — emotion may inject
                // the child later, which previously left the local-screen tile
                // sized by its intrinsic `width: 100%` instead of the row layout's
                // 20% / 150px floor.
                '&& > *': {
                  width: '20%',
                  minWidth: 150,
                  flexShrink: 0,
                },
              },
            }}
          >
            {sidebarParticipantIds.map(id => {
              if (isScreenTileId(id)) {
                const innerSessionId = sessionIdFromTileId(id);
                const isLocal = innerSessionId === localSessionId;
                return (
                  <ParticipantTile
                    key={id}
                    sessionId={innerSessionId}
                    variant="thumbnail"
                    isLocal={isLocal}
                    isScreenShare
                    label={isLocal ? 'Your share' : undefined}
                    hoverHint={isLocal ? 'Click to stop sharing' : undefined}
                    clickAriaLabel={isLocal ? 'Stop sharing your screen' : undefined}
                    onClick={isLocal ? onStopLocalScreenShare : undefined}
                    highlightBorder={isLocal}
                    hideParticipantBadge
                  />
                );
              }
              return (
                <ParticipantTile
                  key={id}
                  sessionId={id}
                  variant="thumbnail"
                  isLocal={id === localSessionId}
                  isActiveSpeaker={id === activeSpeakerId}
                />
              );
            })}
          </Box>
        )}

        {/* Main stage */}
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
          {mainSessionId && (
            <ParticipantTile
              sessionId={mainSessionId}
              variant="main"
              isLocal={mainSessionId === localSessionId}
              isActiveSpeaker={mainSessionId === activeSpeakerId}
              isScreenShare={isScreenShare}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
